import {
  setLoading,
  setLoaded,
  isOperationLoading,
  hasOperationLoaded,
  setError,
  clearError as clearOpError,
} from '@gw/ui';
import * as Sentry from '@sentry/react';

import { ApiError } from '@/api/apiError';
import { organizationsApi } from '@/api/organizations';
import { usersApi } from '@/api/users';
import { auth } from '@/lib/awsCognito.util';
import { getAuthToken } from '@/lib/devStackAuth.util';
import { getActiveOrgIdFromToken } from '@/lib/jwtUtils.util';
import { isFailedToFetchMessage } from '@/lib/networkError.util';

import type {
  LoadingStates,
  LoadedTimestamps,
  ErrorStates,
  Organization,
  OrganizationUsageMetrics,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationRole,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

/**
 * GW-6534: Time-to-live for persisted org data before re-validating against
 * the API. After this many milliseconds, `fetchUserOrganizations` falls
 * through to the network even when localStorage already holds a populated
 * `userOrganizations` and `currentOrg`. Catches cases where a server-side
 * delete/suspend/role-change has invalidated cached state.
 *
 * 5 minutes balances freshness against rate-limit pressure on the
 * `users/me/organizations` endpoint.
 */
const ORG_STALENESS_TTL_MS = 5 * 60 * 1000;

/**
 * GW-9525: Module-scoped monotonic "auth generation" counter. `signOut` bumps
 * it (via {@link invalidateOrgFetches}) as its FIRST synchronous side effect,
 * so any `fetchUserOrganizations` that was in flight during signOut can detect
 * it resolved into a signed-out session and skip re-mirroring a stale
 * `gw-current-org-id` back into localStorage + `currentOrg`.
 *
 * It is deliberately module-scoped rather than store state: `signOut` performs
 * a full store reset, which would wipe a state-held counter, whereas this
 * survives the reset and stays monotonic across the signOut boundary.
 */
let authGeneration = 0;

/**
 * GW-9525: Bump the auth generation so any in-flight org fetch invalidates its
 * captured generation and skips all org-id mirroring writes on resolve. Called
 * by `signOut` before the `auth.signOut()` await so a fetch resolving during
 * that await window is also invalidated.
 */
export function invalidateOrgFetches(): void {
  authGeneration += 1;
}

// GW-411: Type for pending user invitations (invitations TO the user, not from org)
// GW-661: Added invitationCode field - needed for accepting invitations
export interface PendingUserInvitation {
  invitationId: string;
  invitationCode: string;
  organizationId: string;
  orgName: string;
  email: string;
  role: string;
  status: string;
  expiresAt: number;
  createdAt: string;
}

export interface OrganizationSlice {
  // State
  currentOrg: Organization | null;
  userOrganizations: Organization[];
  usageMetrics: OrganizationUsageMetrics | null; // Token balance and usage data
  members: OrganizationMember[]; // Organization members
  invitations: OrganizationInvitation[]; // Pending invitations
  pendingUserInvitations: PendingUserInvitation[]; // GW-411: Invitations pending for current user
  isLoading: LoadingStates; // Object with keys: fetch, select, update, fetchUsage, fetchMembers, fetchInvitations, etc.
  hasLoaded: LoadingStates; // Object tracking which operations have completed
  lastLoaded: LoadedTimestamps; // Object tracking timestamps of completed operations
  errors: ErrorStates; // Object tracking errors per operation
  hasRefreshedOrg: boolean; // GW-548: Guard to prevent infinite refresh loops

  // Actions - Organization
  fetchUserOrganizations: () => Promise<void>;
  selectOrganization: (orgId: string) => Promise<void>;
  updateOrganization: (orgId: string, updates: Partial<Organization>) => Promise<void>;
  fetchOrganizationUsage: () => Promise<void>; // Fetch token balance and usage metrics
  clearError: (operation?: string) => void; // Clear error for specific operation or all errors
  setCurrentOrganization: (org: Organization | null) => void;

  // Actions - Members
  fetchMembers: () => Promise<void>;
  inviteMember: (email: string, role: OrganizationRole) => Promise<void>;
  updateMemberRole: (userId: string, role: OrganizationRole) => Promise<void>;
  disableMember: (userId: string) => Promise<void>;
  enableMember: (userId: string) => Promise<void>;

  // Actions - Invitations
  fetchInvitations: () => Promise<void>;
  resendInvitation: (invitationId: string) => Promise<void>;
  revokeInvitation: (invitationId: string) => Promise<void>;

  // Actions - User Pending Invitations (GW-411)
  fetchPendingUserInvitations: () => Promise<void>;
  acceptUserInvitation: (invitationCode: string) => Promise<void>;
  clearPendingUserInvitations: () => void;

  // Actions - Refresh (GW-509)
  refreshCurrentOrganization: (forceRefresh?: boolean) => Promise<void>;

  // Actions - Reset (for logout)
  resetOrganizationState: () => void;
}

const initialOrganizationState = {
  currentOrg: null,
  userOrganizations: [],
  usageMetrics: null,
  members: [] as OrganizationMember[],
  invitations: [] as OrganizationInvitation[],
  pendingUserInvitations: [] as PendingUserInvitation[], // GW-411
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  hasRefreshedOrg: false, // GW-548: Guard to prevent infinite refresh loops
};

/**
 * GW-9327: Resolve the membership matching the JWT active-org claim
 * (`custom:orgId`), the org the token is actually scoped to for backend
 * authorization. Returns the matching `Organization` from `organizations` when
 * the claim is present AND corresponds to a membership; otherwise `null`, so
 * callers fall through to their existing selection fallback. Fails open — a
 * `getSession()` error or a missing/malformed idToken yields `null`.
 */
async function resolveJwtActiveOrg(organizations: Organization[]): Promise<Organization | null> {
  try {
    const sessionResult = await auth.getSession();
    if (!sessionResult.success || !sessionResult.tokens) {
      return null;
    }
    const idToken = sessionResult.tokens.idToken?.toString();
    if (!idToken) {
      return null;
    }
    const activeOrgId = getActiveOrgIdFromToken(idToken);
    if (!activeOrgId) {
      return null;
    }
    return organizations.find((o) => o.id === activeOrgId) ?? null;
  } catch (error) {
    console.warn('[OrganizationSlice] Unable to resolve JWT active org:', error);
    return null;
  }
}

export const createOrganizationSlice: StateCreator<OrganizationSlice, [], [], OrganizationSlice> = (
  set,
  get
) => ({
  ...initialOrganizationState,

  fetchUserOrganizations: async () => {
    // GW-9525: Capture the auth generation at entry. If signOut bumps it while
    // this fetch is in flight, the org-id mirroring writes below are skipped so
    // a resolving-after-signOut fetch cannot repopulate a stale
    // `gw-current-org-id`.
    const fetchGeneration = authGeneration;
    const isCurrentGeneration = () => fetchGeneration === authGeneration;

    const currentState = get();

    // E2E Debug: Log guard checks

    // Guard: Don't fetch if already loading or if we've already fetched successfully
    if (isOperationLoading(currentState.isLoading, 'fetch')) {
      return;
    }
    if (hasOperationLoaded(currentState.hasLoaded, 'fetch')) {
      return;
    }
    // Guard: Skip fetch if userOrganizations is already populated from localStorage hydration
    // This prevents overwriting mock data in E2E tests or persisted data after page reload.
    //
    // GW-6534: Stale-org TTL revalidation. If the persisted `currentOrg` is
    // older than ORG_STALENESS_TTL_MS, fall through to the network fetch so
    // a server-side delete/suspend/role-change reflects in cache. Without
    // this, a dead orgId can survive in cache until logout.
    if (currentState.userOrganizations.length > 0 && currentState.currentOrg) {
      const lastFetchedAt = currentState.lastLoaded?.fetch ?? 0;
      const age = Date.now() - lastFetchedAt;
      if (age < ORG_STALENESS_TTL_MS) {
        const loadedState = setLoaded(
          currentState.hasLoaded,
          currentState.lastLoaded,
          'fetch',
          true
        );
        set({ hasLoaded: loadedState.hasLoaded, lastLoaded: loadedState.lastLoaded });
        return;
      }
      // Else: persisted data is older than the TTL — fall through and
      // re-validate against the API.
    }

    // Guard: Check localStorage directly in case Zustand hasn't hydrated yet
    // Zustand's persist middleware hydrates asynchronously, so the state check above
    // may see empty arrays even when localStorage has data.
    // Instead of just returning and waiting for hydration, we immediately populate
    // the in-memory state from localStorage to avoid race conditions (e.g., LoginPage
    // checking userOrganizations.length immediately after calling this function).
    try {
      const storage = localStorage.getItem('gw-storage');
      if (storage) {
        const parsed = JSON.parse(storage);
        if (parsed?.state?.userOrganizations?.length > 0 && parsed?.state?.currentOrg) {
          // Immediately populate in-memory state from localStorage
          const loadedState = setLoaded(
            currentState.hasLoaded,
            currentState.lastLoaded,
            'fetch',
            true
          );
          set({
            userOrganizations: parsed.state.userOrganizations,
            currentOrg: parsed.state.currentOrg,
            hasLoaded: loadedState.hasLoaded,
            lastLoaded: loadedState.lastLoaded,
          });
          return;
        }
      }
    } catch {
      // Invalid JSON or localStorage not available, proceed with fetch
    }

    // Guard: Verify auth token is available before making the API call.
    // On full page reload, Zustand persist hydration races with Amplify's fetchAuthSession().
    // If the token isn't ready yet, the request fires without Authorization → API Gateway
    // returns 401 without CORS headers → browser throws TypeError: Failed to fetch.
    // In that case, mark as loaded to prevent infinite retry; the page will re-trigger
    // via ProtectedRoute once Cognito hydration completes.
    const token = await getAuthToken();
    if (!token) {
      const freshState = get();
      const loadedState = setLoaded(freshState.hasLoaded, freshState.lastLoaded, 'fetch', true);
      set({ hasLoaded: loadedState.hasLoaded, lastLoaded: loadedState.lastLoaded });
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetch', true),
      errors: clearOpError(currentState.errors, 'fetch'),
    });

    try {
      const organizations = await usersApi.getUserOrganizations();

      // GW-9525: Skip ALL state repopulation and org-id mirroring if the
      // session is signed out, so a fetch cannot resurrect a stale
      // `gw-current-org-id` (which signOut just cleared) or a stale
      // `currentOrg`. Two independent signals cover the two race shapes:
      //   1. `!isCurrentGeneration()` — signOut bumped the generation while a
      //      fetch initiated BEFORE it was in flight.
      //   2. `!isAuthenticated` — a fetch INITIATED after signOut captures the
      //      new generation (so guard 1 is satisfied) but must still bail. By
      //      the time this post-await guard runs, signOut's synchronous state
      //      reset has already set `isAuthenticated: false`; the fetch's own
      //      network awaits guarantee this check executes on a later tick.
      if (
        !isCurrentGeneration() ||
        !(get() as unknown as { isAuthenticated: boolean }).isAuthenticated
      ) {
        return;
      }

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetch', true);

      set({
        userOrganizations: organizations,
        isLoading: setLoading(state.isLoading, 'fetch', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      // Always update currentOrg if we have one selected (to get fresh data like roles)
      const existingCurrentOrg = get().currentOrg;

      // GW-9327: Resolve the active-org claim (custom:orgId) from the JWT.
      // Backend authorization keys EXCLUSIVELY on this claim, so whenever it is
      // present AND matches one of the user's memberships it MUST win over a
      // stale in-memory selection, stale localStorage, or a first-in-list
      // default. Otherwise `currentOrg` diverges from the token's active org and
      // org-scoped requests (e.g. GET token balance) target an org the token is
      // not scoped to, which the backend rejects with 403. When no claim is
      // available (or it doesn't match a membership), the existing fallback
      // behavior below is preserved unchanged.
      const jwtActiveOrg = await resolveJwtActiveOrg(organizations);

      // GW-9525: `resolveJwtActiveOrg` awaits `auth.getSession()`, so signOut
      // could have fired during THIS await too. Re-check both the generation
      // AND `isAuthenticated` (see the guard above the first await for why both
      // signals are required) before any of the selection branches below write
      // `currentOrg` / `gw-current-org-id`, otherwise a fetch resolving into a
      // signed-out session would resurrect the org id signOut just cleared.
      if (
        !isCurrentGeneration() ||
        !(get() as unknown as { isAuthenticated: boolean }).isAuthenticated
      ) {
        return;
      }

      // If user only has one org, auto-select it (or refresh it if already selected)
      if (organizations.length === 1) {
        const org = organizations[0];
        set({ currentOrg: org });
        localStorage.setItem('gw-current-org-id', org.id);
      } else if (jwtActiveOrg) {
        // GW-9327: honor the JWT active-org claim over every stale fallback.
        set({ currentOrg: jwtActiveOrg });
        localStorage.setItem('gw-current-org-id', jwtActiveOrg.id);
      } else if (organizations.length > 0 && existingCurrentOrg) {
        // User has multiple orgs and one is already selected - refresh it with new data
        const refreshedOrg = organizations.find((o) => o.id === existingCurrentOrg.id);
        if (refreshedOrg) {
          set({ currentOrg: refreshedOrg });
        }
      } else if (organizations.length > 0) {
        // Check if we have a stored org selection
        const storedOrgId = localStorage.getItem('gw-current-org-id');
        if (storedOrgId) {
          const org = organizations.find((o) => o.id === storedOrgId);
          if (org) {
            set({ currentOrg: org });
          } else {
            // Stored org not found in user's orgs - select first available
            const firstOrg = organizations[0];
            set({ currentOrg: firstOrg });
            localStorage.setItem('gw-current-org-id', firstOrg.id);
          }
        } else {
          // No stored selection - auto-select first organization
          const firstOrg = organizations[0];
          set({ currentOrg: firstOrg });
          localStorage.setItem('gw-current-org-id', firstOrg.id);
        }
      }
    } catch (error) {
      // TypeError: Failed to fetch is a CORS/network failure from API Gateway returning 401
      // without CORS headers when the auth token isn't ready yet. This is a transient race
      // condition, not an application logic error — demote to warn so gotoAndCheck doesn't
      // capture it as a test failure.
      //
      // GW-8963: usersApi.getUserOrganizations() runs through a BaseApiClient subclass, which
      // now wraps the raw TypeError('Failed to fetch') into an ApiError tagged with
      // { __networkError: true }. Recognize both forms: the raw TypeError (fetch paths that
      // bypass BaseApiClient) AND the wrapped ApiError, so the benign demotion still applies.
      //
      // GW-9113: Classification is prefix-based (via `isFailedToFetchMessage`) rather than an
      // exact `===` match, so browser variants that append the failing URL to the message are
      // also recognized as benign.
      const isNetworkError =
        (error instanceof TypeError && isFailedToFetchMessage(error.message)) ||
        (error instanceof ApiError &&
          (error.data as { __networkError?: boolean } | undefined)?.__networkError === true);
      if (isNetworkError) {
        console.warn(
          '[OrganizationSlice] Network error fetching organizations (auth race?):',
          error
        );
      } else {
        console.error('[OrganizationSlice] Failed to fetch organizations:', error);
      }

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to load organizations';
      // Mark as loaded (true) even on error to prevent infinite retry loops
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetch', true);

      set({
        isLoading: setLoading(state.isLoading, 'fetch', false),
        errors: setError(state.errors, 'fetch', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  selectOrganization: async (orgId: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'select', true),
      errors: clearOpError(currentState.errors, 'select'),
    });

    try {
      // Call API to select organization and get refreshed JWT
      const response = await usersApi.selectOrganization(orgId);

      // Push the new org-scoped JWT into Amplify's session cache so that
      // subsequent fetchAuthSession() calls return the new token immediately.
      // Without this refresh, every API call after org switch uses the old
      // Cognito-cached JWT and operates in the wrong org context (GW-5515).
      //
      // GW-9136: the backend (handleSelectOrganization) never returns a `token`
      // field — only `refreshToken: true` (see gw-functions/functions/users/handler/
      // main.go). This guard used to check `response.token`, which was never truthy
      // in production, so the refresh never fired and the org switch was cosmetic
      // only. Cognito's custom:orgId attribute is already updated server-side by the
      // time this response arrives; refreshSession() takes no arguments and just
      // force-refreshes the client's own session against Cognito to pick up the new
      // claim, so `refreshToken` is the correct (and only) signal to gate on.
      if (response.refreshToken) {
        await auth.refreshSession();
      }

      // Find and set the selected organization
      const org = get().userOrganizations.find((o) => o.id === orgId);
      if (org) {
        const state = get();
        const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'select', true);

        // GW-4414: Clear the datasets cache when switching orgs. Datasets are org-scoped,
        // so stale data from the previous org must not block re-fetching for the new org.
        // Reset hasLoaded.list/listOwned so fetchDatasets will re-fetch on the next call.
        // GW-8565: also clear owned-mode listing state (listOwned guard + the
        // ownedDatasets pool + cursor/hasMore) so org-scoped owned data never
        // leaks across org switches.
        // GW-9834: extend the same clear to EVERY org-scoped, un-keyed global slot so
        // data from the previous org never leaks into the next org after a switch.
        // Each field is reset to its owning slice's verified initial value. Relates
        // to GW-9831. Notable: hasCheckedStatus MUST be reset — checkPublisherAgreement
        // Status short-circuits at `if (state.hasCheckedStatus[orgId]) return` before
        // re-fetching, so leaving a stale flag would keep publisherAgreementStatus
        // pinned to the previous org on an A -> B -> A switch-back.
        // GW-9831: also clear the org-scoped API key. `orgApiKey` (DatasetsSlice)
        // is a single global slot NOT keyed by orgId, so a key fetched for the
        // previous org would otherwise leak into the new org's view (stale key
        // flash + a falsely-GREEN "Set up API key" checklist) until the API Key
        // page's own fetch resolves to 404 and clears it.
        // The `set` function operates on the full combined store at runtime, so we cast
        // to set cross-slice fields (these live in their own slices, not OrganizationSlice).
        (set as (partial: Record<string, unknown>) => void)({
          currentOrg: org,
          isLoading: setLoading(state.isLoading, 'select', false),
          hasLoaded: { ...loadedState.hasLoaded, list: false, listOwned: false },
          lastLoaded: loadedState.lastLoaded,
          // datasetsSlice (GW-4414 / GW-8565)
          datasets: [],
          ownedDatasets: [],
          ownedCursor: undefined,
          ownedHasMore: false,
          // organizationSlice (this slice)
          usageMetrics: null,
          members: [],
          invitations: [],
          // publisherAgreementSlice (publisherAgreementStatus is persisted)
          publisherAgreementStatus: null,
          hasCheckedStatus: {},
          // organizationTokensSlice
          organizationBalance: null,
          organizationSubscription: null,
          organizationTransactions: [],
          // applicationsSlice
          applications: [],
          orgApplicationsPagination: null,
          orgApplicationsSearchQuery: '',
          // serviceOfferingsSlice
          orgOfferings: [],
          orgOfferingsNextCursor: null,
          // referralCodesSlice
          referralCodes: [],
          currentReferralCode: null,
          // giftCardsSlice
          giftCards: [],
          currentGiftCard: null,
          // orgInvoiceRequestsSlice
          invoiceRequests: [],
          // organizationTransactionsSlice
          orgTransactions: [],
          // analyticsSlice
          usageAnalytics: null,
          applicationUsageAnalytics: null,
          // datasetsSlice (GW-9831)
          orgApiKey: null,
        });
        // GW-9525: defense-in-depth — only mirror the org id while signed in so
        // a selection resolving into a signed-out session cannot resurrect the
        // key signOut just cleared.
        if ((get() as unknown as { isAuthenticated: boolean }).isAuthenticated) {
          localStorage.setItem('gw-current-org-id', orgId);
        }
      } else {
        throw new Error('Organization not found');
      }
    } catch (error) {
      console.error('[OrganizationSlice] Failed to select organization:', error);
      // GW-9273: defense-in-depth — capture in Sentry here as well as in the
      // HeaderHost catch (the primary user-facing surface).
      Sentry.captureException(error, { tags: { feature: 'org-switch' } });

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to select organization';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'select', false);

      set({
        isLoading: setLoading(state.isLoading, 'select', false),
        errors: setError(state.errors, 'select', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  updateOrganization: async (orgId: string, updates: Partial<Organization>) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'update', true),
      errors: clearOpError(currentState.errors, 'update'),
    });

    try {
      const updatedOrg = await organizationsApi.updateOrganization(orgId, updates);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'update', true);

      // GW-1427: Preserve user roles when API response omits them.
      // PUT /organizations/{orgId} returns org metadata but not user-specific roles.
      // Roles come from the user's membership record, not the org entity itself.
      const withRoles = (apiOrg: typeof updatedOrg, fallback: typeof updatedOrg | null) => ({
        ...apiOrg,
        // Empty array also falls back intentionally: the org-update endpoint never
        // returns membership roles, so an empty response means "roles not provided",
        // not "user has no roles".
        roles: apiOrg.roles?.length ? apiOrg.roles : (fallback?.roles ?? []),
      });

      // Update in userOrganizations list
      set({
        userOrganizations: state.userOrganizations.map((org) =>
          org.id === orgId ? withRoles(updatedOrg, org) : org
        ),
        currentOrg:
          state.currentOrg?.id === orgId
            ? withRoles(updatedOrg, state.currentOrg)
            : state.currentOrg,
        isLoading: setLoading(state.isLoading, 'update', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to update organization:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to update organization';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'update', false);

      set({
        isLoading: setLoading(state.isLoading, 'update', false),
        errors: setError(state.errors, 'update', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  setCurrentOrganization: (org: Organization | null) => {
    set({ currentOrg: org });
    if (org) {
      // GW-9525: defense-in-depth — never mirror an org id into localStorage
      // while signed out, so a post-signout caller cannot resurrect the key
      // signOut just cleared. The removeItem branch always runs (clearing is
      // always safe).
      if ((get() as unknown as { isAuthenticated: boolean }).isAuthenticated) {
        localStorage.setItem('gw-current-org-id', org.id);
      }
    } else {
      localStorage.removeItem('gw-current-org-id');
    }
  },

  fetchOrganizationUsage: async () => {
    const currentState = get();

    // Guard: Don't fetch if no current org — calling /organizations/me/usage without
    // org context in the JWT will fail with 500 or 403 (GW-6231)
    if (!currentState.currentOrg) {
      return;
    }

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchUsage')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchUsage', true),
      errors: clearOpError(currentState.errors, 'fetchUsage'),
    });

    try {
      const usageMetrics = await organizationsApi.getOrganizationUsage();

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchUsage', true);

      set({
        usageMetrics,
        isLoading: setLoading(state.isLoading, 'fetchUsage', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to fetch usage metrics:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load organization usage metrics';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchUsage', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchUsage', false),
        errors: setError(state.errors, 'fetchUsage', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  clearError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },

  // ============================================================================
  // Members Actions
  // ============================================================================

  fetchMembers: async () => {
    const currentState = get();

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchMembers')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchMembers', true),
      errors: clearOpError(currentState.errors, 'fetchMembers'),
    });

    try {
      const members = await organizationsApi.getOrganizationMembers();

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchMembers', true);

      set({
        members,
        isLoading: setLoading(state.isLoading, 'fetchMembers', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to fetch members:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load organization members';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchMembers', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchMembers', false),
        errors: setError(state.errors, 'fetchMembers', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  inviteMember: async (email: string, role: OrganizationRole) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'inviteMember', true),
      errors: clearOpError(currentState.errors, 'inviteMember'),
    });

    try {
      await organizationsApi.inviteMember(email, role);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'inviteMember', true);

      set({
        isLoading: setLoading(state.isLoading, 'inviteMember', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      // Refresh invitations list after successful invite
      await get().fetchInvitations();
    } catch (error) {
      console.error('[OrganizationSlice] Failed to invite member:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to invite member';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'inviteMember', false);

      set({
        isLoading: setLoading(state.isLoading, 'inviteMember', false),
        errors: setError(state.errors, 'inviteMember', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  updateMemberRole: async (userId: string, role: OrganizationRole) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'updateMemberRole', true),
      errors: clearOpError(currentState.errors, 'updateMemberRole'),
    });

    // Optimistic update
    const previousMembers = currentState.members;
    set({
      members: currentState.members.map((member) =>
        member.userId === userId ? { ...member, roles: [role] } : member
      ),
    });

    try {
      await organizationsApi.updateMemberRoles(userId, [role]);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'updateMemberRole', true);

      set({
        isLoading: setLoading(state.isLoading, 'updateMemberRole', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to update member role:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to update member role';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'updateMemberRole', false);

      // Rollback optimistic update
      set({
        members: previousMembers,
        isLoading: setLoading(state.isLoading, 'updateMemberRole', false),
        errors: setError(state.errors, 'updateMemberRole', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  disableMember: async (userId: string) => {
    const currentState = get();
    const orgId = currentState.currentOrg?.id;
    if (!orgId) {
      throw new Error(
        '[OrganizationSlice] Cannot disable member: no current organization selected'
      );
    }
    set({
      isLoading: setLoading(currentState.isLoading, 'disableMember', true),
      errors: clearOpError(currentState.errors, 'disableMember'),
    });

    // Optimistic update
    const previousMembers = currentState.members;
    set({
      members: currentState.members.map((member) =>
        member.userId === userId ? { ...member, status: 'suspended' as const } : member
      ),
    });

    try {
      await organizationsApi.updateMemberStatus(orgId, userId, 'suspended');

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'disableMember', true);

      set({
        isLoading: setLoading(state.isLoading, 'disableMember', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to disable member:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to disable member';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'disableMember', false);

      // Rollback optimistic update
      set({
        members: previousMembers,
        isLoading: setLoading(state.isLoading, 'disableMember', false),
        errors: setError(state.errors, 'disableMember', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  enableMember: async (userId: string) => {
    const currentState = get();
    const orgId = currentState.currentOrg?.id;
    if (!orgId) {
      throw new Error('[OrganizationSlice] Cannot enable member: no current organization selected');
    }
    set({
      isLoading: setLoading(currentState.isLoading, 'enableMember', true),
      errors: clearOpError(currentState.errors, 'enableMember'),
    });

    // Optimistic update
    const previousMembers = currentState.members;
    set({
      members: currentState.members.map((member) =>
        member.userId === userId ? { ...member, status: 'active' as const } : member
      ),
    });

    try {
      await organizationsApi.updateMemberStatus(orgId, userId, 'active');

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'enableMember', true);

      set({
        isLoading: setLoading(state.isLoading, 'enableMember', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to enable member:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to enable member';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'enableMember', false);

      // Rollback optimistic update
      set({
        members: previousMembers,
        isLoading: setLoading(state.isLoading, 'enableMember', false),
        errors: setError(state.errors, 'enableMember', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // Invitations Actions
  // ============================================================================

  fetchInvitations: async () => {
    const currentState = get();

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchInvitations')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchInvitations', true),
      errors: clearOpError(currentState.errors, 'fetchInvitations'),
    });

    try {
      const response = await organizationsApi.getOrganizationInvitations();

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchInvitations', true);

      set({
        invitations: response.invitations,
        isLoading: setLoading(state.isLoading, 'fetchInvitations', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to fetch invitations:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load organization invitations';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'fetchInvitations', false);

      set({
        isLoading: setLoading(state.isLoading, 'fetchInvitations', false),
        errors: setError(state.errors, 'fetchInvitations', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  resendInvitation: async (invitationId: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'resendInvitation', true),
      errors: clearOpError(currentState.errors, 'resendInvitation'),
    });

    try {
      await organizationsApi.resendInvitation(invitationId);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'resendInvitation', true);

      set({
        isLoading: setLoading(state.isLoading, 'resendInvitation', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      // Refresh invitations to update lastSentAt
      await get().fetchInvitations();
    } catch (error) {
      console.error('[OrganizationSlice] Failed to resend invitation:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend invitation';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'resendInvitation', false);

      set({
        isLoading: setLoading(state.isLoading, 'resendInvitation', false),
        errors: setError(state.errors, 'resendInvitation', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  revokeInvitation: async (invitationId: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'revokeInvitation', true),
      errors: clearOpError(currentState.errors, 'revokeInvitation'),
    });

    // Optimistic update - remove invitation from list
    const previousInvitations = currentState.invitations;
    set({
      invitations: currentState.invitations.filter((inv) => inv.invitationId !== invitationId),
    });

    try {
      await organizationsApi.revokeInvitation(invitationId);

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'revokeInvitation', true);

      set({
        isLoading: setLoading(state.isLoading, 'revokeInvitation', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to revoke invitation:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to revoke invitation';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'revokeInvitation', false);

      // Rollback optimistic update
      set({
        invitations: previousInvitations,
        isLoading: setLoading(state.isLoading, 'revokeInvitation', false),
        errors: setError(state.errors, 'revokeInvitation', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // User Pending Invitations Actions (GW-411)
  // ============================================================================

  fetchPendingUserInvitations: async () => {
    const currentState = get();

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, 'fetchPendingUserInvitations')) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, 'fetchPendingUserInvitations', true),
      errors: clearOpError(currentState.errors, 'fetchPendingUserInvitations'),
    });

    try {
      const response = await organizationsApi.getUserPendingInvitations();

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchPendingUserInvitations',
        true
      );

      set({
        pendingUserInvitations: response.invitations ?? [],
        isLoading: setLoading(state.isLoading, 'fetchPendingUserInvitations', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      console.error('[OrganizationSlice] Failed to fetch pending user invitations:', error);

      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load pending invitations';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'fetchPendingUserInvitations',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'fetchPendingUserInvitations', false),
        errors: setError(state.errors, 'fetchPendingUserInvitations', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  acceptUserInvitation: async (invitationCode: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'acceptUserInvitation', true),
      errors: clearOpError(currentState.errors, 'acceptUserInvitation'),
    });

    try {
      await organizationsApi.acceptInvitation(invitationCode);

      const state = get();
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'acceptUserInvitation',
        true
      );

      set({
        isLoading: setLoading(state.isLoading, 'acceptUserInvitation', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      // Refresh pending invitations and user organizations after accepting
      await get().fetchPendingUserInvitations();
      // GW-663: Reset fetch hasLoaded AND clear userOrganizations to bypass ALL guards
      // in fetchUserOrganizations (the guard at line 112-114 checks userOrganizations.length > 0)
      const afterAcceptState = get();
      set({
        hasLoaded: { ...afterAcceptState.hasLoaded, fetch: false },
        userOrganizations: [], // GW-663: Clear to bypass guard condition
      });
      await get().fetchUserOrganizations();
    } catch (error) {
      console.error('[OrganizationSlice] Failed to accept invitation:', error);

      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept invitation';
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'acceptUserInvitation',
        false
      );

      set({
        isLoading: setLoading(state.isLoading, 'acceptUserInvitation', false),
        errors: setError(state.errors, 'acceptUserInvitation', errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  clearPendingUserInvitations: () => {
    set({ pendingUserInvitations: [] });
  },

  // GW-509: Force refresh organization data to get updated fields like stripeConnectedAt
  // GW-548: Added guards to prevent infinite refresh loops
  // forceRefresh: boolean - Set to true to bypass the hasRefreshedOrg guard (use for critical updates like Stripe completion)
  refreshCurrentOrganization: async (forceRefresh = false) => {
    const currentState = get();

    // GW-548: Guard - Don't refresh if already refreshing
    if (isOperationLoading(currentState.isLoading, 'refresh')) {
      return;
    }

    // GW-548: Guard - Don't refresh if already refreshed (unless forceRefresh is true)
    if (currentState.hasRefreshedOrg && !forceRefresh) {
      return;
    }

    // Set loading state
    set({
      isLoading: setLoading(currentState.isLoading, 'refresh', true),
    });

    // Reset hasLoaded.fetch AND clear userOrganizations to bypass ALL guards in fetchUserOrganizations
    // Guard at line 130 checks: userOrganizations.length > 0 && currentOrg
    // We need to clear userOrganizations to force a fresh API fetch
    const stateAfterLoading = get();
    const currentOrgId = stateAfterLoading.currentOrg?.id; // Save current org ID to restore selection
    set({
      hasLoaded: { ...stateAfterLoading.hasLoaded, fetch: false },
      userOrganizations: [], // Clear to bypass guard at line 130
    });

    // Re-fetch organizations - this will update userOrganizations and currentOrg
    await get().fetchUserOrganizations();

    // If we had a current org selected, ensure it's still selected after refresh
    const refreshedState = get();
    if (currentOrgId && refreshedState.userOrganizations.length > 0) {
      const refreshedOrg = refreshedState.userOrganizations.find((o) => o.id === currentOrgId);
      if (refreshedOrg) {
        set({ currentOrg: refreshedOrg });
      }
    }

    // GW-548: Mark as refreshed and clear loading state
    set({
      hasRefreshedOrg: true,
      isLoading: setLoading(get().isLoading, 'refresh', false),
    });
  },

  resetOrganizationState: () => {
    localStorage.removeItem('gw-current-org-id');
    set({
      ...initialOrganizationState,
    });
  },
});
